import { useEffect } from 'react';

/* usePublishModalTop — tell every fixed pop-up where the content panel starts.
 *
 * InnerTabsm and InnerTabsd call this with a ref to their content panel. It writes
 * `--fr-modal-top` (the panel's top edge, in px) onto <html>; every modal backdrop in the
 * app reads it as its `top`, so a form or modal opens UNDER the TabBar, the inner tab row and
 * the info bar instead of hiding them. Re-measured on resize / rotation and when the panel
 * moves; removed on unmount so pages without inner tabs get full-screen overlays again.
 * It also publishes `--fr-modal-max`: the height a modal may use under that edge (viewport
 * minus the edge, minus 104px of backdrop padding / safe area). House rule for every modal:
 * header and footer stay put, the body scrolls, and the body never exceeds 60vh.
 * `deps` re-runs the measurement (e.g. the active inner tab).
 */
export default function usePublishModalTop(areaRef, deps = []) {
  useEffect(() => {
    const root = document.documentElement;
    const publish = () => {
      const el = areaRef.current;
      if (!el) return;
      const top = Math.max(0, Math.round(el.getBoundingClientRect().top));
      root.style.setProperty('--fr-modal-top', `${top}px`);
      root.style.setProperty('--fr-modal-max', `calc(100dvh - ${top}px - 104px - env(safe-area-inset-bottom, 0px))`);
    };
    publish();
    const raf = requestAnimationFrame(publish);           // again after fonts/layout settle
    window.addEventListener('resize', publish);
    window.addEventListener('orientationchange', publish);
    const ro = typeof ResizeObserver === 'function' && areaRef.current?.parentElement
      ? new ResizeObserver(publish) : null;
    if (ro) ro.observe(areaRef.current.parentElement);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', publish);
      window.removeEventListener('orientationchange', publish);
      if (ro) ro.disconnect();
      root.style.removeProperty('--fr-modal-top');
      root.style.removeProperty('--fr-modal-max');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
